import { api } from './client';
import type { Paged } from './income';

export type BlogLang = 'bg' | 'en';

/**
 * How long a loaded article or index page is trusted before it is asked for again. A minute, which
 * is also how long the server keeps a rendered page — and the reason it is stated at all: the
 * library's default is zero, under which the copy the server embeds in the page is "stale" the
 * instant it is read and gets fetched again on mount, the one request that copy exists to avoid.
 */
export const BLOG_STALE_TIME = 60_000;

export interface BlogTag {
  slug: string;
  name: string;
}

export interface BlogTagCount extends BlogTag {
  count: number;
}

export interface BlogPostSummary {
  slug: string;
  language: 'Bg' | 'En';
  title: string;
  excerpt: string;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  tags: BlogTag[];
  publishedAt: string;
  readingMinutes: number;
}

export interface BlogPost {
  id: string;
  slug: string;
  language: 'Bg' | 'En';
  title: string;
  metaTitle: string | null;
  metaDescription: string | null;
  excerpt: string;
  /** Sanitised by the server when it was saved — the only reason it may be written into the page. */
  bodyHtml: string;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  ogImageUrl: string | null;
  tags: BlogTag[];
  faq: { question: string; answer: string }[];
  sources: { title: string; url: string }[];
  publishedAt: string | null;
  updatedAt: string | null;
  readingMinutes: number;
  wordCount: number;
  authorName: string;
  aiGenerated: boolean;
  parameterYear: number | null;
  /** The published version in the other language, when there is one. */
  alternateSlug: string | null;
  noIndex: boolean;
  isPreview: boolean;
  related: BlogPostSummary[];
}

export const blogApi = {
  posts: (lang: BlogLang, page: number, tag?: string | null) => {
    const query = new URLSearchParams({ lang, page: String(page), pageSize: '12' });
    if (tag) query.set('tag', tag);
    return api<Paged<BlogPostSummary>>(`/api/public/blog/posts?${query}`);
  },

  post: (lang: BlogLang, slug: string, preview?: string | null) =>
    api<BlogPost>(
      `/api/public/blog/posts/${lang}/${encodeURIComponent(slug)}${preview ? `?preview=${encodeURIComponent(preview)}` : ''}`,
    ),

  tags: (lang: BlogLang) => api<BlogTagCount[]>(`/api/public/blog/tags?lang=${lang}`),
};
